import { useEffect } from "react";

import {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { GenericObject } from "src/api/flowTypes";
import {
  organizationsApi,
  organizationUsersApi,
  organizationWsUsersApi,
  usersApi,
} from "src/api/taktileApi";
import {
  OrganizationAuthSourceCreateNamed,
  OrganizationAuthSourceResponse,
  OrganizationUser,
  User,
  WorkspaceSimpleRoleType,
} from "src/clients/taktile-api";

type OrganizationUsersOptions = {
  includeDeactivated?: boolean;
  limit?: number;
};

export const organizationUsersKey = {
  all: ["organizationUsers"],
  list: (orgId: string, options?: OrganizationUsersOptions) =>
    options
      ? [...organizationUsersKey.all, "list", orgId, options]
      : [...organizationUsersKey.all, "list", orgId],
};

export const organizationAuthSourceKey = {
  all: ["organizationAuthSource"],
  list: (orgId: string) => [...organizationAuthSourceKey.all, "list", orgId],
};

export const useOrganizationUser = (orgId: string, userId?: string) => {
  return useQuery<OrganizationUser, AxiosError>({
    queryKey: [userId],
    queryFn: async () =>
      (
        await organizationUsersApi.getUserApiV1OrganizationsOrgIdUsersUserIdGet(
          orgId,
          userId!,
        )
      ).data,
    enabled: !!userId,
    retry: (failureCount, error) => {
      const status = error.response?.status;
      if (status === 404 || status === 429) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

const PAGE_SIZE = 30;

// Track active auto-fetch operations by query key to prevent multiple hook instances
// from triggering concurrent fetch chains
const autoFetchInProgress = new Map<string, boolean>();

const useAutoFetchAllPages = <T>(
  queryKey: QueryKey,
  result: UseInfiniteQueryResult<InfiniteData<T[]>, Error>,
): void => {
  const keyString = JSON.stringify(queryKey);

  useEffect(() => {
    // Only one instance should trigger fetches for a given query key
    if (autoFetchInProgress.get(keyString)) return;

    if (!result.isFetchingNextPage && result.hasNextPage) {
      autoFetchInProgress.set(keyString, true);
      result.fetchNextPage().finally(() => {
        autoFetchInProgress.set(keyString, false);
      });
    }
  }, [keyString, result.isFetchingNextPage, result.hasNextPage, result]);
};

export const useOrganizationUsers = (
  orgId: string,
  options?: OrganizationUsersOptions,
) => {
  const { includeDeactivated, limit = PAGE_SIZE } = options ?? {};

  return useInfiniteQuery<
    OrganizationUser[],
    Error,
    InfiniteData<OrganizationUser[]>,
    QueryKey,
    number
  >({
    queryKey: organizationUsersKey.list(orgId, { includeDeactivated, limit }),
    queryFn: async ({ pageParam }): Promise<OrganizationUser[]> =>
      (
        await organizationUsersApi.getUsersApiV1OrganizationsOrgIdUsersGet(
          orgId,
          includeDeactivated,
          pageParam,
          limit,
        )
      ).data,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === limit ? pages.length * limit : undefined,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAllOrganizationUsers = (
  orgId: string,
  includeDeactivated?: boolean,
) => {
  const options = { includeDeactivated, limit: 60 };
  const usersResult = useOrganizationUsers(orgId, options);

  useAutoFetchAllPages(organizationUsersKey.list(orgId, options), usersResult);

  return usersResult;
};

export const useOrganizationAuthSources = (orgId: string) => {
  return useQuery<OrganizationAuthSourceResponse[], Error>({
    queryKey: organizationAuthSourceKey.list(orgId),
    queryFn: async () => {
      const response =
        await organizationsApi.getOrganizationAuthSourcesApiV1OrganizationsOrganizationIdAuthSourcesGet(
          orgId,
        );

      return response.data;
    },
  });
};

export const useDeleteOrganizationAuthSource = (
  orgId: string,
  authSourceId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      organizationsApi.deleteOrganizationAuthSourceApiV1OrganizationsOrganizationIdAuthSourcesAuthSourceIdDelete(
        orgId,
        authSourceId,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: organizationAuthSourceKey.list(orgId),
      });
    },
  });
};

export const useCreateOrganizationAuthSource = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (authSource: OrganizationAuthSourceCreateNamed) =>
      organizationsApi.postOrganizationAuthSourceApiV1OrganizationsOrganizationIdAuthSourcesPost(
        orgId,
        authSource,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: organizationAuthSourceKey.list(orgId),
      });
    },
  });
};

export const useMigrateUsersAuthSource = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    {
      message: string;
      organization_id: string;
      domain: string;
      new_source_name: string;
      affected_users_count: number;
    },
    Error,
    { authSourceId: string; domain: string }
  >({
    mutationFn: async ({ authSourceId, domain }) => {
      const response =
        await organizationsApi.migrateUsersAuthSourceApiV1OrganizationsOrganizationIdMigrateUsersAuthSourceAuthSourceIdPost(
          orgId,
          authSourceId,
          { domain },
        );
      return response.data as {
        message: string;
        organization_id: string;
        domain: string;
        new_source_name: string;
        affected_users_count: number;
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationAuthSourceKey.list(orgId),
      });
    },
  });
};

export const workspaceUsersKey = {
  all: ["workspaceUsers"],
  list: (wsId: string, params?: GenericObject) => [
    ...workspaceUsersKey.all,
    "list",
    wsId,
    JSON.stringify(params),
  ],
};

type WorkspaceUsersParams = {
  include_deactivated?: boolean;
  workspace_role_filters?: WorkspaceSimpleRoleType[];
  limit?: number;
};

export const useWorkspaceUsers = (
  orgId: string,
  wsId: string,
  params?: WorkspaceUsersParams,
) => {
  return useQuery<OrganizationUser[], Error>({
    queryKey: workspaceUsersKey.list(wsId, params),
    queryFn: async () =>
      (
        await organizationWsUsersApi.getUsersApiV1OrganizationsOrgIdWorkspacesWorkspaceIdUsersGet(
          orgId,
          wsId,
          params?.include_deactivated ?? true,
          params?.workspace_role_filters
            ? new Set(params.workspace_role_filters)
            : undefined,
          undefined,
          params?.limit || 200,
        )
      ).data,
    enabled: !!orgId && !!wsId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
  });
};

export const useUserById = (userId?: string) => {
  return useQuery<User, Error>({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await usersApi.getUserByIdApiV1UsersIdUserIdGet(userId!);
      return response.data;
    },
    enabled: !!userId,
  });
};
